'use client'

import React from 'react'
import { z } from 'zod'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'

import { useForgotPassword } from '@/api/client'

import { Stack } from '@/components/layout/stack'
import { Text } from '@/components/typography/text'
import { FormInput } from '@/components/form/input/FormInput'
import { Button } from '@/components/inputs/button'
import { Form } from '@/components/form/Form'
import { Spinner } from '@/components/icons/spinner/Spinner'
import { zodResolver } from '@hookform/resolvers/zod'

import * as styles from '@/screens/auth/sign-in/SignIn.css'

const forgotPasswordSchema = z.object({
	email: z.string().email({ message: 'Please enter a valid email address' }).min(1, { message: 'Email is required' })
})

type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>

export const ForgotPasswordScreen = () => {
	const forgotPasswordMutation = useForgotPassword()

	const form = useForm<ForgotPasswordSchema>({
		resolver: zodResolver(forgotPasswordSchema),
		defaultValues: {
			email: ''
		}
	})

	const submit = async (data: ForgotPasswordSchema) => {
		try {
			const res = await forgotPasswordMutation.mutateAsync(data)

			res && form.reset()
		} catch (e) {
			console.error(e)
		}
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(submit)}>
				<Stack gap={12}>
					<Text as="h2" variant="h1" textAlign="center">
						Forgot Password
					</Text>
					<p className={clsx(styles.largeText)}>
						Enter your email address and we'll send you a link to reset your password.
					</p>
					<FormInput name="email" label="Email" placeholder="Email" />
					<Button disabled={forgotPasswordMutation.isPending}>
						{forgotPasswordMutation.isPending && <Spinner spinnerColor="white" />}
						Send
					</Button>
				</Stack>
			</form>
		</Form>
	)
}
